import { Injectable } from '@angular/core';

import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ProjectCarrierMapDTO, ProjectGroup, ProjectDTO, Owner } from 'cargo-chief-commons';

import { State } from '@state/core.state';

import { ProjectDetailsState, initialState } from '../state';
import {
	loadingProjectCarrierMaps,
	engagedCarriersList,
	procuredCarriersList,
	engagedCarriersCount,
	removingEngagedCarriersInProgress,
	projectGroups,
	procuredCarriersCount,
	loadingAccountCarrierLanes,
	accountCarrierLanes,
	procuredCarriersWithoutGroupCount,
	projectDTO,
	loadingProject,
	updatingProject,
	searchTruckForm,
	projectsNextPage,
	isLoadingProjectsList,
	projectsPage,
	totalNumberOfProjects,
	projectsPageSize,
	allProjectsList,
	usersList,
	allProjectCarrierMapsList,
	carrierAllocations,
	carrierAllocationsMap,
	loadingCarrierAllocations,
	allocatedCarriersList,
	allocatedCarriersCount,
} from '../selectors/project-carrier-map-selector';
import { AllocationStatusDTO } from '@core/services/allocation/allocation.service';

@Injectable({ providedIn: 'root' })
export class ProjectDetailsStore extends ComponentStore<ProjectDetailsState> {
	public readonly loadingProjectCarrierMaps$: Observable<boolean> = this.store.pipe(
		select(loadingProjectCarrierMaps)
	);

	public readonly projectDTO$: Observable<ProjectDTO> = this.store.pipe(select(projectDTO));
	public readonly loadingProject$: Observable<boolean> = this.store.pipe(select(loadingProject));
	public readonly updatingProject$: Observable<boolean> = this.store.pipe(select(updatingProject));
	public readonly searchTruckForm$: Observable<any> = this.store.pipe(select(searchTruckForm));

	public readonly engagedCarriersList$: Observable<ProjectCarrierMapDTO[]> = this.store.pipe(
		select(engagedCarriersList)
	);
	public readonly engagedCarriersCount$: Observable<number> = this.store.pipe(
		select(engagedCarriersCount)
	);

	public readonly procuredCarriersList$: Observable<ProjectCarrierMapDTO[]> = this.store.pipe(
		select(procuredCarriersList)
	);
	public readonly procuredCarriersCount$: Observable<number> = this.store.pipe(
		select(procuredCarriersCount)
	);
	public readonly procuredCarriersWithoutGroupCount$: Observable<number> = this.store.pipe(
		select(procuredCarriersWithoutGroupCount)
	);

	public readonly removingEngagedCarriersInProgress$: Observable<boolean> = this.store.pipe(
		select(removingEngagedCarriersInProgress)
	);

	public readonly projectGroups$: Observable<ProjectGroup[]> = this.store.pipe(
		select(projectGroups),
		debounceTime(100),
		distinctUntilChanged()
	);

	public readonly loadingAccountCarrierLanes$: Observable<boolean> = this.store.pipe(
		select(loadingAccountCarrierLanes)
	);
	public readonly accountCarrierLanes$: Observable<any> = this.store.pipe(
		select(accountCarrierLanes)
	);

	public readonly projectsNextPage$: Observable<any> = this.store.pipe(select(projectsNextPage));
	public readonly isLoadingProjectsList$: Observable<any> = this.store.pipe(
		select(isLoadingProjectsList)
	);
	public readonly projectsPage$: Observable<any> = this.store.pipe(select(projectsPage));
	public readonly projectsPageSize$: Observable<any> = this.store.pipe(select(projectsPageSize));
	public readonly totalNumberOfProjects$: Observable<any> = this.store.pipe(
		select(totalNumberOfProjects)
	);
	public readonly allProjectsList$: Observable<any> = this.store.pipe(select(allProjectsList));

	public readonly usersList$: Observable<Owner[]> = this.store.pipe(select(usersList));

	public readonly allProjectCarrierMapsList$: Observable<ProjectCarrierMapDTO[]> = this.store.pipe(
		select(allProjectCarrierMapsList)
	);

	public readonly carrierAllocations$: Observable<AllocationStatusDTO[]> = this.store.pipe(
		select(carrierAllocations)
	);
	public readonly carrierAllocationsMap$: Observable<{ [carrierId: string]: AllocationStatusDTO }> =
		this.store.pipe(select(carrierAllocationsMap));
	public readonly loadingCarrierAllocations$: Observable<boolean> = this.store.pipe(
		select(loadingCarrierAllocations)
	);

	// Allocated carriers for Allocations tab
	public readonly allocatedCarriersList$: Observable<ProjectCarrierMapDTO[]> = this.store.pipe(
		select(allocatedCarriersList)
	);
	public readonly allocatedCarriersCount$: Observable<number> = this.store.pipe(
		select(allocatedCarriersCount)
	);

	constructor(private readonly store: Store<State>) {
		super(initialState);
	}

	public readonly setProcuredCarriersCount = this.updater(
		(state, procuredCarriersCount: number) => ({
			...state,
			procuredCarriersCount,
		})
	);

	public readonly clear = this.updater(() => ({ ...initialState }));
}
